import * as React from 'react';
import {useSelector} from 'react-redux';

import {selectAgencyData} from 'src/selectors/agency';
import {useQueryObject} from 'src/hooks/useQueryObject';
import {BYOD_DATE_RANGE_QUERY_KEY} from 'src/components/analytics-genesis/byod/constants';
import {getByodDefaultDateRange} from 'src/components/analytics-genesis/byod/utils/default-date-range';
import type {DateRange} from 'src/types/byod';


type UseByodDateRangeReturn = readonly [DateRange, (next: DateRange) => void];

// Every BYOD date consumer (the DashboardBuilder picker, the report viewer, and
// the edit panel) calls this hook with the same query key, so they all read and
// write the same slot. No store, no context, no prop drilling. The builder reads
// the range here and injects it into the canvas as a tile filter — the tiles
// themselves (`useTileData`) are store-free and never read this hook directly.
//
// Side benefit: the selected range is shareable (copy URL → same range) and
// browser back/forward steps through range changes.
export function useByodDateRange(): UseByodDateRangeReturn {
  const {timezone: agencyTimezone} = useSelector(selectAgencyData);

  // Default: last 7 days in the agency timezone — the builder's date-picker
  // starting range (getByodDefaultDateRange). The embeddable view has NO default;
  // it requires an explicit host date window. Memoized so the reference is stable
  // across renders within a consumer — useQueryObject's defaultValue should be
  // stable to avoid redundant parses.
  const defaultDateRange = React.useMemo<DateRange>(
    () => getByodDefaultDateRange(agencyTimezone),
    [agencyTimezone],
  );

  const [dateRange, update] = useQueryObject<DateRange>(
    BYOD_DATE_RANGE_QUERY_KEY,
    defaultDateRange,
  );

  // Wrap the heterogeneous setter from useQueryObject in a single-shape
  // setter so callers don't have to think about partial-vs-section forms.
  const setDateRange = React.useCallback(
    (next: DateRange) => update(next),
    [update],
  );

  return [dateRange, setDateRange] as const;
}
